export function hasDiscoveryChildren(button) {
  return Array.isArray(button?.children) && button.children.length > 0;
}

function validateDiscoveryButtonNode(btn, pathLabel, texts = {}) {
  if (!btn || typeof btn !== 'object') {
    return { ok: false, error: `${pathLabel}: ${texts.validationInvalidData}` };
  }
  if (!String(btn.text ?? '').trim()) {
    return { ok: false, error: `${pathLabel}: ${texts.validationMissingButtonText}` };
  }

  const children = btn.children;
  if (children != null && !Array.isArray(children)) {
    return { ok: false, error: `${pathLabel}: ${texts.validationChildrenMustBeArray}` };
  }

  if (hasDiscoveryChildren(btn)) {
    for (let i = 0; i < children.length; i += 1) {
      const childResult = validateDiscoveryButtonNode(
        children[i],
        `${pathLabel} ← ${(texts.validationChildLabel || '').replace('{n}', String(i + 1))}`,
        texts
      );
      if (!childResult.ok) {
        return childResult;
      }
    }
    return { ok: true };
  }

  if (!String(btn.message ?? '').trim()) {
    return { ok: false, error: `${pathLabel}: ${texts.validationMissingBotMessage}` };
  }

  return { ok: true };
}

export function validateDiscoveryButtons(buttons, texts = {}) {
  if (buttons == null) {
    return { ok: true, value: [] };
  }

  if (!Array.isArray(buttons)) {
    return { ok: false, error: texts.validationDiscoveryMustBeArray };
  }

  for (let i = 0; i < buttons.length; i += 1) {
    const result = validateDiscoveryButtonNode(
      buttons[i],
      (texts.validationButtonLabel || '').replace('{n}', String(i + 1)),
      texts
    );
    if (!result.ok) {
      return result;
    }
  }

  return { ok: true, value: buttons };
}

export function emptyDiscoveryButton() {
  return {
    text: '',
    message: '',
    icon: '',
    children: [],
  };
}

/** Get button at path of indices into nested children, e.g. [0, 2] → root[0].children[2] */
export function getButtonAtPath(buttons, path) {
  if (!Array.isArray(buttons) || !Array.isArray(path) || path.length === 0) {
    return null;
  }

  let current = buttons[path[0]];
  if (!current) return null;

  for (let i = 1; i < path.length; i += 1) {
    const kids = current.children;
    if (!Array.isArray(kids)) return null;
    current = kids[path[i]];
    if (!current) return null;
  }

  return current;
}

/** Immutable patch of a button at path (shallow-merge patch onto that node). */
export function patchButtonAtPath(buttons, path, patch) {
  if (!Array.isArray(buttons) || !Array.isArray(path) || path.length === 0) {
    return buttons;
  }

  const walk = (list, depth) => {
    const index = path[depth];
    if (!Array.isArray(list) || index < 0 || index >= list.length) {
      return list;
    }

    return list.map((btn, i) => {
      if (i !== index) return btn;
      if (depth === path.length - 1) {
        return { ...btn, ...patch };
      }
      const children = Array.isArray(btn.children) ? btn.children : [];
      return { ...btn, children: walk(children, depth + 1) };
    });
  };

  return walk(buttons, 0);
}

/** Remove button at path. */
export function removeButtonAtPath(buttons, path) {
  if (!Array.isArray(buttons) || !Array.isArray(path) || path.length === 0) {
    return buttons;
  }

  if (path.length === 1) {
    return buttons.filter((_, i) => i !== path[0]);
  }

  const parentPath = path.slice(0, -1);
  const parent = getButtonAtPath(buttons, parentPath);
  if (!parent || !Array.isArray(parent.children)) {
    return buttons;
  }

  const childIndex = path[path.length - 1];
  return patchButtonAtPath(buttons, parentPath, {
    children: parent.children.filter((_, i) => i !== childIndex),
  });
}

/** Append a child under parentPath (empty path = root list). Returns { buttons, newPath }. */
export function appendButtonAtParent(buttons, parentPath, newButton) {
  const root = Array.isArray(buttons) ? buttons : [];

  if (!Array.isArray(parentPath) || parentPath.length === 0) {
    const next = [...root, newButton];
    return { buttons: next, newPath: [next.length - 1] };
  }

  const parent = getButtonAtPath(root, parentPath);
  if (!parent) {
    return { buttons: root, newPath: null };
  }

  const children = Array.isArray(parent.children) ? parent.children : [];
  const nextChildren = [...children, newButton];
  const next = patchButtonAtPath(root, parentPath, { children: nextChildren });
  return {
    buttons: next,
    newPath: [...parentPath, nextChildren.length - 1],
  };
}
